import { useEffect, useState } from 'react';
import { api, mainImage, formatSize, formatEdition, imageUrl, peek } from './api';
import ImageViewer from './ImageViewer';
import { LoadingSpinner, Skeleton, toast } from './feedback';
import ArtworkForm from './ArtworkForm';
import QrDialog from './QrDialog';

// Dùng cho admin (id: có nút Quay lại, Sửa, Xuất QR, Xoá) và cho người quét QR (qrToken: chỉ xem)
export default function ArtworkPage({ id, qrToken, onBack, onDeleted, categories = [], materials = [] }) {
  const [artwork, setArtwork] = useState(undefined); // undefined = đang tải, null = không tìm thấy
  const [current, setCurrent] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const isAdmin = !qrToken;

  const show = (found) => {
    setArtwork(found);
    setCurrent((cur) => found.images?.find((img) => img.id === cur?.id) || mainImage(found));
  };

  useEffect(() => {
    // Admin mở lại tranh đã xem: hiện ngay bản đã lưu, bản mới từ máy chủ thay vào sau
    const cached = !qrToken && peek(() => api.getArtwork(id));
    if (cached) show(cached);
    // Admin lấy bản đầy đủ (có chủ sở hữu, mã QR); người quét QR chỉ lấy bản công khai
    (qrToken ? api.getArtworkByQr(qrToken) : api.getArtwork(id))
      .then((found) => {
        show(found);
        if (found.title) document.title = `${found.title} – Kợt Nhả Decor`;
      })
      .catch((e) => {
        if (e.status === 404) setArtwork(null);
        else setError('Không tải được tranh. Vui lòng thử lại sau.');
      });
  }, [id, qrToken]);

  const back = onBack && (
    <button className="back" onClick={() => onBack(artwork)}>
      ← Quay lại
    </button>
  );

  const openQr = async () => {
    try {
      if (!artwork.qrToken) show(await api.ensureQr(artwork.id));
      setShowQr(true);
    } catch (e) {
      toast(`Không tạo được mã QR: ${e.message}`);
    }
  };

  // Chưa xuất QR thì xoá hẳn (chưa ai cầm QR của tranh này); đã xuất QR thì chỉ thu hồi vào thùng rác
  const remove = async () => {
    const permanent = !artwork.qrToken;
    const message = permanent
      ? `Xoá hẳn tranh "${artwork.title}" (${artwork.code})?\n\nTranh chưa xuất QR nên sẽ bị xoá vĩnh viễn cùng toàn bộ ảnh.`
      : `Thu hồi tranh "${artwork.title}" (${artwork.code})?\n\nTranh đã xuất QR nên sẽ vào Thùng rác: ai quét QR sẽ thấy "đã bị thu hồi". Có thể khôi phục lại.`;
    if (!window.confirm(message)) return;
    try {
      if (permanent) await api.removePermanently(artwork.id);
      else await api.remove(artwork.id);
      toast(permanent ? 'Đã xoá tranh' : 'Đã thu hồi tranh (chuyển vào thùng rác)');
      onDeleted(artwork);
    } catch (e) {
      toast(`Xoá lỗi: ${e.message}`);
    }
  };

  const actions = isAdmin && artwork && (
    <div className="detail-actions">
      <button onClick={() => setEditing(true)}>Sửa</button>
      <button onClick={openQr}>{artwork.qrToken ? 'Xem QR' : 'Xuất QR'}</button>
      <button className="danger" onClick={remove}>
        Xoá
      </button>
    </div>
  );

  if (error) return <>{back}<p className="error">{error}</p></>;
  if (artwork === undefined)
    return (
      <section aria-busy="true" aria-label="Đang tải">
        <LoadingSpinner />
        {/* Khung xương cùng bố cục với trang thật: nút trên cùng, ảnh bên trái, thông tin bên phải */}
        <div className="detail-top">
          {back}
          {isAdmin && (
            <div className="detail-actions">
              {[56, 80, 56].map((w, i) => (
                <Skeleton key={i} width={w} height={36} radius={8} />
              ))}
            </div>
          )}
        </div>
        <div className="detail">
          <Skeleton radius={10} style={{ width: '100%', aspectRatio: '4 / 3' }} />
          <div className="detail-info">
            {!isAdmin && <Skeleton width={240} height={34} radius={999} style={{ marginBottom: 16 }} />}
            <Skeleton width="65%" height={30} style={{ marginBottom: 10 }} />
            <Skeleton width="45%" height={14} style={{ marginBottom: 24 }} />
            <dl>
              {[60, 70, 85, 40, 75, 65].map((w, i) => (
                <div key={i} style={{ display: 'contents' }}>
                  <dt><Skeleton width={80} height={14} /></dt>
                  <dd><Skeleton width={`${w}%`} height={14} /></dd>
                </div>
              ))}
            </dl>
            <Skeleton width="35%" height={18} style={{ margin: '28px 0 12px' }} />
            {[100, 95, 70].map((w, i) => (
              <Skeleton key={i} width={`${w}%`} height={13} style={{ marginBottom: 8 }} />
            ))}
          </div>
        </div>
      </section>
    );
  if (artwork === null) return <>{back}<p className="muted">Không tìm thấy tranh này.</p></>;
  // Tranh đã thu hồi (vd quét QR cũ): chỉ báo thu hồi, không hiện thông tin
  if (artwork.status === 'THU_HOI') return <>{back}<p className="warning">⚠ Tranh này đã bị thu hồi.</p></>;

  const images = artwork.images || [];

  return (
    <section>
      <div className="detail-top">
        {back}
        {actions}
      </div>
      <div className="detail">
        <div className="detail-images">
          {current ? (
            <div className="detail-main-wrap">
              <img
                className="detail-main"
                src={imageUrl(current.imageUrl, 1400)}
                alt={artwork.title}
                onClick={() => setViewerOpen(true)}
              />
              <button className="zoom-btn" onClick={() => setViewerOpen(true)}>
                ⤢ Phóng to
              </button>
            </div>
          ) : (
            <div className="detail-main no-image">Chưa có ảnh</div>
          )}
          {images.length > 1 && (
            <div className="thumbs">
              {images.map((img) => (
                <img
                  key={img.id}
                  src={imageUrl(img.imageUrl, 160)}
                  alt=""
                  className={img.id === current?.id ? 'active' : ''}
                  onClick={() => setCurrent(img)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="detail-info">
          {qrToken && (
            <p className="cert-badge">
              <span className="cert-badge-icon">✓</span>
              Chứng nhận chính hãng · Kợt Nhả Decor
            </p>
          )}
          <h1>{artwork.title}</h1>
          <p className="cert-code">
            Mã chứng nhận: <strong>{artwork.code}</strong>
          </p>
          <dl>
            <dt>Phiên bản</dt>
            <dd>{formatEdition(artwork) || '—'}</dd>
            <dt>Kích thước</dt>
            <dd>{formatSize(artwork.size) || '—'}</dd>
            <dt>Chất liệu</dt>
            <dd>{artwork.material || '—'}</dd>
            <dt>Năm phát hành</dt>
            <dd>{artwork.releaseYear || '—'}</dd>
            <dt>Tác giả</dt>
            <dd>{artwork.author || '—'}</dd>
            <dt>Chủ sở hữu</dt>
            <dd>{artwork.ownerName || '—'}</dd>
          </dl>
          {artwork.description && (
            <div className="description">
              <h2>Mô tả tác phẩm</h2>
              <p>{artwork.description}</p>
            </div>
          )}
        </div>
      </div>

      {viewerOpen && <ImageViewer images={images} startId={current.id} onClose={() => setViewerOpen(false)} />}
      {showQr && <QrDialog artwork={artwork} onClose={() => setShowQr(false)} />}
      {editing && (
        <div className="dialog-backdrop" onClick={() => setEditing(false)}>
          <div className="form-dialog" onClick={(e) => e.stopPropagation()}>
            <ArtworkForm
              initial={artwork}
              categories={categories}
              materials={materials}
              onSave={async (data) => {
                show(await api.update(artwork.id, data));
                toast('Đã lưu thay đổi');
                setEditing(false);
              }}
              onCancel={() => setEditing(false)}
            />
          </div>
        </div>
      )}
    </section>
  );
}
